import queryString, { ParsedQuery } from 'query-string';

import { isPointGeotype } from 'algolia-api/search/isPointGeotype';
import { ListingType } from 'algolia-api/types/ListingType';
import { SearchFacet } from 'algolia-api/types/SearchFacet';
import { SearchFilters } from 'algolia-api/types/SearchFilters';
import { SearchFiltersWithType } from 'algolia-api/types/SearchFiltersWithType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import {
  GradSortType,
  SortType,
} from 'modules/main/store/ducks/MainStoreMainSearchState';
import { DEFAULT_SEARCH_RADIUS } from 'utils/constants/general/defaultSearchRadius';
import {
  arrToObject,
  filterNulls,
  filterObject,
  mapObject,
  removeKeys,
  updateObject,
} from 'utils/functional';

const NO_FILTER_SENTINEL = '_NONE_';
const IDEALIST_PARAM_RE = /(\?|&)(idealist)=?/;

type AllSearchTypes = ListingType | StaffTypeFilter;
export type FiltersWithType = SearchFiltersWithType<AllSearchTypes>;
export const makeSerializedUrlFilters = ({
  filters,
  searchFacets,
  initialQueryParams,
  includeTypeInQueryParams,
}: {
  filters: SearchFilters | FiltersWithType;
  searchFacets: Record<string, SearchFacet>;
  initialQueryParams: ParsedQuery;
  includeTypeInQueryParams?: boolean;
}) =>
  removeKeys(
    updateObject(
      // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
      ...Object.entries(
        filterObject(
          filters, // ALL is the default value so don't add it to the url
          // UNLESS it is the `type` param, where we use 'ALL'
          (key, value) => value !== 'ALL' || key === 'type',
        ),
      ).map(([key, value]) => {
        const facet = searchFacets[key];

        if (facet && facet.type === 'date') {
          return value;
        }

        if (
          facet &&
          facet.type === 'string' &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          return { [facet.name]: initialQueryParams[facet.name] };
        }

        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        return facet
          ? // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line no-nested-ternary
            facet.type === 'array'
            ? // TODO: this case shouldn't exist
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line no-nested-ternary, @typescript-eslint/no-explicit-any
              (value as any).length === 0
              ? {
                  [facet.name]: NO_FILTER_SENTINEL,
                }
              : // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              facet.options.every((i) => (value as any).includes(i)) &&
                facet.id !== 'jobFamilyFacet' // will be removed: 'ALL' case
              ? {}
              : {
                  [facet.name]: value,
                }
            : {
                [facet.name]: value,
              }
          : {
              [key]: value,
            };
      }),
    ),
    [includeTypeInQueryParams ? '' : 'type'],
  );

const getSearchUrl = ({
  filters,
  searchFacets,
  query,
  pageIndex,
  sort,
  location,
  radius,
  resultsView,
  scrollToId,
  searchFrom,
  originatingPathname,
  currentSearchParams,
  includeTypeInQueryParams,
}: {
  filters: Readonly<SearchFiltersWithType<AllSearchTypes>>;
  searchFacets: Record<string, SearchFacet>;
  query: string;
  pageIndex: number;
  sort?: Readonly<Record<AllSearchTypes, SortType | GradSortType>>;
  location: SearchLocation;
  radius: string;
  currentSearchParams: string;
  resultsView?: string | null | undefined;
  searchPagePath?: string;
  searchFrom?: string | null | undefined;
  scrollToId?: string;
  originatingPathname?: string;
  includeTypeInQueryParams?: boolean;
}) => {
  // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  const sortParam = (sort && sort[filters.type]) || null;

  const initialQueryParams = queryString.parse(currentSearchParams);

  const newUrl = originatingPathname;
  // No query params for filters (except 'type') with a value of "ALL"
  const urlSerializedFilters = makeSerializedUrlFilters({
    filters,
    searchFacets,
    initialQueryParams,
    includeTypeInQueryParams,
  });
  const searchParams = {
    ...urlSerializedFilters,
    radius,
    resultsView,
    q: query,
    page: pageIndex + 1,
  };

  if (sortParam !== 'relevance') {
    searchParams.sort = sortParam;
  }

  const searchFiltersWithNull = arrToObject(
    Object.values(searchFacets).reduce(
      (acc, v) =>
        // @ts-expect-error TS(2769): No overload matches this call.
        acc.concat(v.names ? v.names.map((n) => [n, null]) : [[v.name, null]]),
      [],
    ),
  );
  const addedValues = filterObject(searchParams, (key, value) => {
    if (
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      (key === 'page' && value && parseInt(value, 10) < 2) ||
      (key === 'radius' &&
        location &&
        (!location.latitude ||
          !isPointGeotype(location.geoType) ||
          value === DEFAULT_SEARCH_RADIUS)) ||
      (key === 'resultsView' && value !== 'map') ||
      (key === 'sort' && query === '') ||
      (key !== 'q' && !value)
    ) {
      return false;
    }

    return true;
  });
  const queryParams = updateObject(
    searchFiltersWithNull,
    mapObject(searchParams, (k) => [k, null]),
    addedValues,
  );

  let formattedCurrentSearchParams = currentSearchParams;

  // the `idealist` param is passed without a value
  // in that case, append an empty value
  if (IDEALIST_PARAM_RE.test(currentSearchParams)) {
    formattedCurrentSearchParams = currentSearchParams.replace(
      IDEALIST_PARAM_RE,
      '$1$2=',
    );
  }

  const returnedParams = filterNulls(
    updateObject(queryString.parse(formattedCurrentSearchParams), queryParams),
  );
  return {
    pathname: newUrl,
    search: `?${queryString.stringify(returnedParams)}`,
    state: scrollToId
      ? {
          searchFrom,
          scrollToId,
        }
      : {
          searchFrom,
        },
  };
};

// eslint-disable-next-line import/no-default-export
export default getSearchUrl;
